import React, { useState, useEffect } from 'react';
import { notificationService, deploymentService, clientService } from '../../services/api';
import { X, Plus, Trash2 } from 'lucide-react';

const TRIGGERS = [
  'deployment_offline','deployment_online','deployment_degraded',
  'cpu_threshold','mem_threshold','disk_threshold','check_failed',
  'incident_created','incident_resolved',
  'ticket_created','ticket_mention','ticket_sla_risk','ticket_sla_breach','ticket_unassigned',
];

const CH_TYPES = ['email', 'webhook', 'slack'];

export default function NotificationRuleModal({ onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [deployments, setDeployments] = useState([]);
  const [clients, setClients]         = useState([]);

  const [form, setForm] = useState({
    name: '', trigger: 'deployment_offline', enabled: true,
    deployment: '', client: '', cooldownMinutes: 30,
    channels: [{ type: 'email', target: '' }],
  });

  useEffect(() => {
    deploymentService.getAll().then(r => setDeployments(r.data.deployments));
    clientService.getAll().then(r => setClients(r.data.clients));
  }, []);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const addChannel    = () => setForm(p => ({ ...p, channels: [...p.channels, { type: 'email', target: '' }] }));
  const removeChannel = (i) => setForm(p => ({ ...p, channels: p.channels.filter((_,idx) => idx !== i) }));
  const updateChannel = (i, key, val) => setForm(p => {
    const ch = [...p.channels]; ch[i] = { ...ch[i], [key]: val }; return { ...p, channels: ch };
  });

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.deployment) delete payload.deployment;
      if (!payload.client)     delete payload.client;
      await notificationService.createRule(payload);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="rule-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>New Notification Rule</h3>
          <button className="icon-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {error && <div className="form-error">{error}</div>}

          <div className="form-group"><label>Rule Name *</label><input value={form.name} onChange={e => f('name', e.target.value)} required placeholder="e.g. Alert team on critical offline" /></div>

          <div className="form-row">
            <div className="form-group">
              <label>Trigger *</label>
              <select value={form.trigger} onChange={e => f('trigger', e.target.value)}>
                {TRIGGERS.map(t => <option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Cooldown (minutes)</label>
              <input type="number" min={0} value={form.cooldownMinutes} onChange={e => f('cooldownMinutes', +e.target.value)} />
            </div>
          </div>

          <div className="form-group"><label>Scope — Deployment (leave blank = all)</label>
            <select value={form.deployment} onChange={e => f('deployment', e.target.value)}>
              <option value="">All Deployments</option>
              {deployments.map(d => <option key={d._id} value={d._id}>{d.name} ({d.client?.name})</option>)}
            </select>
          </div>
          <div className="form-group"><label>Scope — Client</label>
            <select value={form.client} onChange={e => f('client', e.target.value)}>
              <option value="">All Clients</option>
              {clients.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>

          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 8 }}>
              <label style={{ fontSize:12, fontWeight:500, color:'var(--text-secondary)' }}>Notification Channels *</label>
              <button type="button" className="btn btn-ghost btn-sm" onClick={addChannel}><Plus size={12} /> Add Channel</button>
            </div>
            {form.channels.map((ch, i) => (
              <div key={i} className="channel-row">
                <select value={ch.type} onChange={e => updateChannel(i, 'type', e.target.value)} style={{ width: 110 }}>
                  {CH_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
                <input value={ch.target} onChange={e => updateChannel(i, 'target', e.target.value)}
                  placeholder={ch.type === 'email' ? 'team@yourco.com' : ch.type === 'slack' ? 'Slack webhook URL' : 'https://webhook.url'} style={{ flex: 1 }} />
                {form.channels.length > 1 && <button type="button" className="icon-btn" onClick={() => removeChannel(i)}><Trash2 size={13} /></button>}
              </div>
            ))}
          </div>

          <div style={{ display:'flex', justifyContent:'flex-end', gap: 10, borderTop:'1px solid var(--border)', paddingTop: 14 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Create Rule'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
